import { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, MapPin, ChevronDown, Loader2, Phone, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { jobSiteService } from '../../services/jobSiteService';
import Button from '../ui/Button';
import AddressInput from '../ui/AddressInput';

// Mini form for creating new job site inline
function CreateJobSiteForm({ companyId, clientId, onCreated, onCancel, onFormDataChange }) {
  const [formData, setFormData] = useState({
    siteName: '',
    address: '',
    addressLat: null,
    addressLng: null,
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Notify parent when form has data (for confirmation on close)
  useEffect(() => {
    const hasData = !!(
      formData.siteName?.trim() ||
      formData.address?.trim() ||
      formData.contactPerson?.trim() ||
      formData.contactEmail?.trim() ||
      formData.contactPhone?.trim()
    );
    onFormDataChange?.(hasData);
  }, [formData, onFormDataChange]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!formData?.siteName?.trim()) {
      setError('Site name is required');
      return;
    }

    if (!formData?.address?.trim()) {
      setError('Address is required for job sites');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const jobSiteData = {
        ...formData,
        clientId
      };
      const newJobSite = await jobSiteService.createJobSite(jobSiteData, companyId);
      onCreated?.(newJobSite);
    } catch (err) {
      console.error('Failed to create job site:', err);
      setError(err?.message || 'Failed to create job site');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-surface rounded-lg border border-border space-y-3">
      <h4 className="text-sm font-medium text-text-primary">Add New Job Site</h4>

      {error && (
        <div className="p-2 text-sm text-red-400 bg-red-500/10 rounded">
          {error}
        </div>
      )}

      {/* Site Name */}
      <input
        type="text"
        value={formData?.siteName}
        onChange={(e) => setFormData(prev => ({ ...prev, siteName: e?.target?.value }))}
        placeholder="Site Name *"
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
      />

      {/* Address (Required) */}
      <div className="space-y-1">
        <AddressInput
          value={{
            address: formData?.address || '',
            lat: formData?.addressLat,
            lng: formData?.addressLng
          }}
          onChange={({ address, lat, lng }) => {
            setFormData(prev => ({
              ...prev,
              address: address || '',
              addressLat: lat,
              addressLng: lng
            }));
          }}
          placeholder="Search for address... *"
          compact={true}
          allowMapToggle={true}
          mapHeight="200px"
        />
        <p className="text-xs text-text-secondary flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Address is required for job sites
        </p>
      </div>

      {/* Contact Person */}
      <input
        type="text"
        value={formData?.contactPerson}
        onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e?.target?.value }))}
        placeholder="Site Contact Person"
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
      />

      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="email"
          value={formData?.contactEmail}
          onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e?.target?.value }))}
          placeholder="Contact Email"
          className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
        />
        <div className="relative flex items-center">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <span className="absolute left-9 top-1/2 -translate-y-1/2 text-text-secondary text-sm">+61</span>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData?.contactPhone}
            onChange={(e) => {
              const value = e?.target?.value?.replace(/[^0-9]/g, '');
              setFormData(prev => ({ ...prev, contactPhone: value }));
            }}
            placeholder="412 345 678"
            className="w-full pl-[4.5rem] pr-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || !formData?.siteName?.trim() || !formData?.address?.trim()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
              Creating...
            </>
          ) : (
            'Add'
          )}
        </Button>
      </div>
    </div>
  );
}

// Main JobSiteSelect component
export default function JobSiteSelect({
  value, // Selected job site object or ID
  onChange,
  companyId,
  clientId, // Required: filter job sites by this client
  className,
  placeholder = 'Search or select job site...',
  disabled = false,
  error,
  onAddressChange // Callback to notify parent of selected job site's address
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobSites, setJobSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formHasData, setFormHasData] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load job sites for the selected client
  const loadJobSites = async () => {
    if (!companyId || !clientId) {
      setJobSites([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await jobSiteService.getClientJobSites(clientId);
      setJobSites(data || []);
    } catch (err) {
      console.error('Failed to load job sites:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Search job sites
  const searchJobSites = async (term) => {
    if (!companyId || !clientId || !term) {
      loadJobSites();
      return;
    }

    setIsLoading(true);
    try {
      const data = await jobSiteService.searchJobSites(companyId, term, clientId);
      setJobSites(data || []);
    } catch (err) {
      console.error('Failed to search job sites:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load when client changes
  useEffect(() => {
    if (companyId && clientId) {
      loadJobSites();
    } else {
      setJobSites([]);
    }
  }, [companyId, clientId]);

  // Debounced search
  const handleSearchChange = (e) => {
    const term = e?.target?.value;
    setSearchTerm(term);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchJobSites(term);
    }, 300);
  };

  // Click outside to close (with confirmation if form has data)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e?.target)) {
        // If create form is open and has data, show confirmation
        if (showCreateForm && formHasData) {
          setShowDiscardConfirm(true);
          return;
        }
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateForm, formHasData]);

  // Handle discard confirmation
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setShowCreateForm(false);
    setFormHasData(false);
    setIsOpen(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false);
  };

  // Handle selection
  const handleSelect = (jobSite) => {
    onChange?.(jobSite);
    // Notify parent of the address change
    if (jobSite && onAddressChange) {
      onAddressChange({
        address: jobSite?.address,
        lat: jobSite?.addressLat,
        lng: jobSite?.addressLng
      });
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle new job site created
  const handleJobSiteCreated = (newJobSite) => {
    setJobSites(prev => [newJobSite, ...prev]);
    handleSelect(newJobSite);
    setShowCreateForm(false);
  };

  // Clear selection
  const handleClear = () => {
    onChange?.(null);
    if (onAddressChange) {
      onAddressChange(null);
    }
    setSearchTerm('');
  };

  const selectedJobSite = typeof value === 'object' ? value : jobSites?.find(js => js?.id === value);

  // If no client selected, show disabled state with message
  if (!clientId) {
    return (
      <div className={cn('space-y-1', className)}>
        <div className="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-lg opacity-50">
          <MapPin className="w-4 h-4 text-white-shade-2" />
          <span className="text-white-shade-2">Select a client first</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Selected Value / Search Input */}
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 bg-surface border rounded-lg cursor-pointer transition-colors',
          isOpen ? 'border-primary-orange' : 'border-border',
          error && 'border-red-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {selectedJobSite ? (
          <>
            <MapPin className="w-4 h-4 text-white-shade-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-text-primary truncate block">
                {selectedJobSite?.siteName}
              </span>
              {selectedJobSite?.address && (
                <span className="text-xs text-text-secondary truncate block">
                  {selectedJobSite?.address}
                </span>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleClear();
                }}
                className="text-white-shade-2 hover:text-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </>
        ) : (
          <>
            <Search className="w-4 h-4 text-white-shade-2 flex-shrink-0" />
            <span className="flex-1 text-white-shade-2">{placeholder}</span>
            <ChevronDown className="w-4 h-4 text-white-shade-2" />
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-400">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border bg-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search job sites..."
                autoFocus
                className="w-full pl-9 pr-3 py-2 bg-surface border border-border rounded text-text-primary text-sm placeholder-gray-500 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Create New Option */}
          {showCreateForm ? (
            <div className="p-2 border-b border-border bg-background">
              <CreateJobSiteForm
                companyId={companyId}
                clientId={clientId}
                onCreated={handleJobSiteCreated}
                onCancel={() => {
                  if (formHasData) {
                    setShowDiscardConfirm(true);
                  } else {
                    setShowCreateForm(false);
                  }
                }}
                onFormDataChange={setFormHasData}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="w-full px-4 py-2 text-left text-sm text-primary-orange hover:bg-surface flex items-center gap-2 border-b border-border bg-background"
            >
              <Plus className="w-4 h-4" />
              Add New Job Site
            </button>
          )}

          {/* Results List */}
          <div className="max-h-48 overflow-y-auto bg-background">
            {isLoading ? (
              <div className="px-4 py-3 text-center text-text-secondary text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Loading...
              </div>
            ) : jobSites?.length > 0 ? (
              jobSites?.map(jobSite => (
                <button
                  key={jobSite?.id}
                  type="button"
                  onClick={() => handleSelect(jobSite)}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-surface transition-colors',
                    selectedJobSite?.id === jobSite?.id && 'bg-surface'
                  )}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-text-secondary flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary truncate">{jobSite?.siteName}</p>
                      {jobSite?.address && (
                        <p className="text-xs text-text-secondary truncate">
                          {jobSite?.address}
                        </p>
                      )}
                      {jobSite?.contactPerson && (
                        <p className="text-xs text-text-secondary truncate mt-0.5">
                          Contact: {jobSite?.contactPerson}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-center text-text-secondary text-sm">
                {searchTerm ? 'No results found' : 'No job sites for this client yet'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCancelDiscard}
          />
          <div className="relative bg-surface border border-border rounded-lg p-4 shadow-xl max-w-sm mx-4">
            <h4 className="text-sm font-medium text-text-primary mb-2">Discard changes?</h4>
            <p className="text-sm text-text-secondary mb-4">
              You have unsaved data in the form. Are you sure you want to discard it?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelDiscard}
              >
                Keep Editing
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleConfirmDiscard}
              >
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
